import { type RefObject, useEffect, useRef, useState } from 'react';

const DEFAULT_ROOT_MARGIN = '600px 0px';

export interface UseOnceVisibleResult<T extends Element> {
    ref: RefObject<T | null>;
    isVisible: boolean;
}

/**
 * Reports whether the observed element has ever intersected the viewport. Once true, it stays
 * true even if the element later scrolls out of view again: this is meant to gate one-time setup
 * work (like kicking off a data fetch), not to toggle repeatedly as the user scrolls.
 */
// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export function useOnceVisible<T extends Element>(rootMargin: string = DEFAULT_ROOT_MARGIN): UseOnceVisibleResult<T> {
    const ref = useRef<T>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isVisible) {
            return;
        }
        const element = ref.current;
        if (!element) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setIsVisible(true);
                }
            },
            { rootMargin, threshold: 0.01 },
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [isVisible, rootMargin]);

    return { ref, isVisible };
}
